import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import logoHorizontal from "@/assets/stoltzfus-logo-horizontal.png.asset.json";

type NavLink = { label: string; href: string; type: "scroll" | "route" };

const navLinks: NavLink[] = [
  { label: "Kitchens", href: "/kitchens", type: "route" },
  { label: "Bathrooms", href: "/bathrooms", type: "route" },
  
  { label: "Gallery", href: "#gallery", type: "scroll" },
  
  { label: "Contact", href: "#contact", type: "scroll" },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const location = useLocation();

  const handleScrollClick = (href: string) => {
    setIsOpen(false);
    if (location.pathname !== "/") {
      window.location.href = "/" + href;
      return;
    }
    setTimeout(() => {
      const el = document.querySelector(href);
      el?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  return (
    <nav
      aria-label="Main navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-background ${
        scrolled || isOpen ? "shadow-sm" : "shadow-none"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 flex items-center justify-between h-24 gap-4">
        <Link
          to="/"
          onClick={() => {
            if (location.pathname === "/") {
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }}
          className="flex items-center group shrink min-w-0 h-full"
          aria-label="Stoltzfus Cabinet Shop home"
        >
          <div className="h-full flex-1 min-w-0 overflow-hidden flex items-center">
            <img
              src={logoHorizontal.url}
              alt="Stoltzfus Cabinet Shop"
              className="h-[180%] w-auto max-w-none object-contain -ml-[8%]"
              fetchPriority="high"
            />
          </div>
        </Link>




        {/* Desktop */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) =>
            link.type === "route" ? (
              <Link
                key={link.href}
                to={link.href}
                className="text-xs tracking-[0.2em] uppercase transition-colors text-foreground/80 hover:text-accent"
              >
                {link.label}
              </Link>
            ) : (
              <button
                key={link.href}
                onClick={() => handleScrollClick(link.href)}
                className="text-xs tracking-[0.2em] uppercase transition-colors text-foreground/80 hover:text-accent"
              >
                {link.label}
              </button>
            )
          )}
        </div>

        {/* Mobile toggle */}
        <button
          aria-label={isOpen ? "Close menu" : "Open menu"}
          aria-expanded={isOpen}
          className="md:hidden min-w-[48px] min-h-[48px] flex items-center justify-center text-foreground"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-background border-b border-border"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {navLinks.map((link) =>
                link.type === "route" ? (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setIsOpen(false)}
                    className="text-left text-sm tracking-widest uppercase text-foreground hover:text-accent transition-colors py-2"
                  >
                    {link.label}
                  </Link>
                ) : (
                  <button
                    key={link.href}
                    onClick={() => handleScrollClick(link.href)}
                    className="text-left text-sm tracking-widest uppercase text-foreground hover:text-accent transition-colors py-2"
                  >
                    {link.label}
                  </button>
                )
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
